import { Button, Dropdown } from '@beeinventor/dasiot-react-component-lib';
import { styled } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AddSvgIcon from '../../components/SvgIcon/AddSvgIcon';
import ArrowUpSvgIcon from '../../components/SvgIcon/ArrowUpSvgIcon';
import ArrowDownSvgIcon from '../../components/SvgIcon/ArrowDownSvgIcon';
import { useState } from 'react';
import CrossSvgIcon from '../../components/SvgIcon/CrossSvgIcon';
import { format } from 'date-fns';
import ConfirmDialog from '../../components/Dialog/ConfirmDialog';
import deletePng from '../../assets/png/delete.png';
import ProgressingDialog from '../../components/Dialog/ProgressingDialog';
import UploadFirmwareDialog, {
  FIRMWARE_DEVICE_TYPES,
  FirmwareDeviceType,
  UploadFirmwareGroup,
} from './UploadFirmwareDialog';
import {
  deleteDassensesFirmware,
  deleteFirmware,
  getDassensesFirmwares,
  getFirmwares,
  uploadDassensesFirmwares,
  uploadFirmwares,
} from '../../apis/firmwareApi';
import { useAppSelector } from '../../hooks';

const MainContainer = styled('main')`
  flex: 1;
  padding: 20px 48px 48px;
  overflow-y: auto;
`;

const OperationContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const ContainerDropdown = styled('div', { label: 'Container-Dropdown' })`
  & > .Dropdown--disabled {
    background: #e5e5e5;
    color: ${({ theme }) => theme.color.secondary.$80};
  }
  & div[role='tooltip'] {
    & > .MuiBox-root {
      color: ${({ theme }) => theme.color.secondary.$80};
      background: #ffffff;
      max-height: 200px;
      overflow: scroll;
    }
    z-index: 5;
  }
`;

const ListContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  & > .list-title {
    text-align: center;
    font-size: 0.875rem;
    margin: 20px 0;
    padding: 14px 48px;

    &:first-of-type {
      text-align: start;
    }
    &:nth-of-type(3) {
      text-align: end;
    }
  }
`;

const FirmwareRowStyled = styled('div')`
  grid-column: 1 / 4;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.1);
  margin: 0 0 8px 0;

  & > .group-title {
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    background-color: #fff;
    padding: 16px;
  }

  & > .item-container {
    background-color: ${({ theme }) => theme.color.secondary.$40};
    & > .item {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      color: ${({ theme }) => theme.color.secondary.$60};
      text-align: center;
      padding: 20px 16px 0 0;

      &:last-of-type {
        padding-bottom: 20px;
      }

      & > div {
        display: flex;
        justify-content: center;
        align-items: center;

        &:nth-of-type(2) {
          font-size: 0.875rem;
        }

        &:nth-of-type(3) {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          text-align: end;
        }

        svg.delete-firmware-button {
          cursor: pointer;
          width: 32px;
          height: 32px;
          color: ${({ theme }) => theme.color.secondary.$100};
        }
      }
    }
  }

  svg {
    display: block;
  }
`;

type FirmwareItemData = {
  name: string;
  hardwareVersion: string;
  createdAt: Date;
};

type FirmwareRowProps = {
  firmwareVersion: string;
  data: FirmwareItemData[];
  onClickDelete: (firmwareVersion: string, fileName: string) => void;
};

const FirmwareRow: React.FC<FirmwareRowProps> = ({
  firmwareVersion,
  data,
  onClickDelete,
}) => {
  const [isExpand, setIsExpand] = useState(false);

  const items = data.map((d) => {
    return (
      <div key={`${firmwareVersion}-${d.hardwareVersion}`} className="item">
        <div></div>
        <div>{d.hardwareVersion}</div>
        <div>
          {format(d.createdAt, 'yyyy-MM-dd HH:mm')}{' '}
          <CrossSvgIcon
            className="delete-firmware-button"
            onClick={() => onClickDelete(firmwareVersion, d.name)}
          />
        </div>
      </div>
    );
  });

  return (
    <FirmwareRowStyled className="group-container">
      <div className="group-title" onClick={() => setIsExpand((prev) => !prev)}>
        <div className="expand-icon">
          {isExpand ? (
            <ArrowUpSvgIcon sx={{ width: '40px', height: '40px' }} />
          ) : (
            <ArrowDownSvgIcon sx={{ width: '40px', height: '40px' }} />
          )}
        </div>
        {firmwareVersion}
      </div>
      {isExpand && <div className="item-container">{items}</div>}
    </FirmwareRowStyled>
  );
};

const FirmwareManagementPage = () => {
  const filterText = useAppSelector((store) => store.system.filter.text);
  const queryClient = useQueryClient();
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<FirmwareDeviceType>('dasloop');
  const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isProcessingDialog, setIsProcessingDialog] = useState(false);
  const [deletingTarget, setDeletingTarget] = useState<{
    firmwareVersion: string;
    fileName: string;
  } | null>(null);

  const { data: firmwaresData } = useQuery({
    queryKey: ['firmwares', selectedDeviceType],
    queryFn: () =>
      selectedDeviceType === 'dassenses'
        ? getDassensesFirmwares().then((res) => res.data.data)
        : getFirmwares().then((res) => res.data.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (params: { firmwareVersion: string; fileName: string }) =>
      selectedDeviceType === 'dassenses'
        ? deleteDassensesFirmware(params)
        : deleteFirmware(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['firmwares', selectedDeviceType],
      });
      setIsProcessingDialog(false);
      setIsOpenDeleteDialog(false);
      setDeletingTarget(null);
    },
    onError: () => {
      setIsProcessingDialog(false);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (groups: UploadFirmwareGroup[]) =>
      Promise.all(
        groups.map((g) =>
          selectedDeviceType === 'dassenses'
            ? uploadDassensesFirmwares(g)
            : uploadFirmwares(g),
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['firmwares', selectedDeviceType],
      });
      setIsOpenUploadDialog(false);
    },
  });

  const handleOnClickDeleteDialog = (
    firmwareVersion: string,
    fileName: string,
  ) => {
    setDeletingTarget({ firmwareVersion, fileName });
    setIsOpenDeleteDialog(true);
  };

  const handleOnCloseDeleteDialog = () => {
    setIsOpenDeleteDialog(false);
    setDeletingTarget(null);
  };

  const handleOnSubmitDeleteFirmware = () => {
    if (!deletingTarget) return;
    setIsProcessingDialog(true);
    deleteMutation.mutate({
      firmwareVersion: deletingTarget.firmwareVersion,
      fileName: deletingTarget.fileName,
    });
  };

  return (
    <>
      <MainContainer className="firmware-management-main">
        <OperationContainer className="operaion-container">
          <ContainerDropdown>
            <Dropdown
              sx={{
                display: 'inline-flex',
                maxWidth: '220px',
                paddingTop: 0,
                paddingBottom: 0,
              }}
              selectedId={selectedDeviceType}
              list={FIRMWARE_DEVICE_TYPES}
              placeholder="Device Type"
              popperProps={{
                disablePortal: true,
                sx: { color: '#606060' },
              }}
              onSelect={(v) => setSelectedDeviceType(v as FirmwareDeviceType)}
            />
          </ContainerDropdown>
          <Button
            sx={{
              fontWeight: '500',
              padding: '0 16px 0 32px',
            }}
            variant="contained"
            color="primary"
            endIcon={<AddSvgIcon sx={{ width: '32px', height: '32px' }} />}
            onClick={() => setIsOpenUploadDialog(true)}
          >
            Upload New Firmware
          </Button>
        </OperationContainer>
        <ListContainer>
          <div className="list-title">Firmware Version</div>
          <div className="list-title">Hardware Version</div>
          <div className="list-title">Upload Date</div>
          {(() => {
            const rows = (firmwaresData ?? [])
              .filter((fw) => fw.files.length > 0)
              .filter((fw) =>
                fw.firmwareVersion
                  .toLowerCase()
                  .includes(filterText.toLowerCase()),
              );
            if (rows.length === 0) {
              return (
                <div
                  style={{
                    gridColumn: '1 / 4',
                    textAlign: 'center',
                    padding: '48px 0',
                    color: '#a1a1a1',
                    fontSize: '0.875rem',
                  }}
                >
                  No Data
                </div>
              );
            }
            return rows.map((fw) => (
              <FirmwareRow
                key={fw.firmwareVersion}
                firmwareVersion={fw.firmwareVersion}
                data={fw.files.map((f) => ({
                  name: f.name,
                  hardwareVersion: f.hardwareVersion,
                  createdAt: new Date(f.createdAt),
                }))}
                onClickDelete={handleOnClickDeleteDialog}
              />
            ));
          })()}
        </ListContainer>
      </MainContainer>
      <UploadFirmwareDialog
        open={isOpenUploadDialog}
        onClose={() => setIsOpenUploadDialog(false)}
        onUpload={(groups) => uploadMutation.mutate(groups)}
        deviceType={selectedDeviceType}
        onDeviceTypeChange={setSelectedDeviceType}
      />
      <ConfirmDialog
        title="Confirm Delete?"
        description="This will permanently delete the file. This action cannot be reversed."
        open={isOpenDeleteDialog}
        imageUrl={deletePng}
        onSubmit={handleOnSubmitDeleteFirmware}
        onClose={handleOnCloseDeleteDialog}
      />
      <ProgressingDialog open={isProcessingDialog} />
    </>
  );
};

export default FirmwareManagementPage;
